/**
 * Hazari — server-authoritative rules engine (4 players, every player for themselves, highest score wins).
 *
 * A simplified version of the Indian "thousand" trick game:
 *  - 4 players, one 52-card deck, all 13 cards dealt at once. There is no trump.
 *  - The player left of the dealer leads. Players must follow the led suit when able; otherwise any card.
 *  - The highest card of the led suit wins the trick and leads the next one.
 *  - Scoring per hand: the winner of a trick scores the values of the cards in it — A 11, 10 10, K 4, Q 3, J 2, the
 *    rest 0 — which is 120 points per hand, and the winner of the last trick scores 10 more (130 in all).
 *  - The match ends when a player reaches `targetScore` (and the highest total is unique) or after `maxHands`.
 *    The highest total wins; equal totals share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface HazariConfig {
  /** Match ends when a player reaches this total (and the highest total is unique). */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
  trickEndDelayMs: number;
}

export type HazariPhase = 'PLAYING' | 'TRICK_END' | 'HAND_END' | 'MATCH_END';

export interface HazariTrickPlay {
  seat: number;
  card: CardId;
}

export interface HazariCompletedTrick {
  leader: number;
  plays: HazariTrickPlay[];
  winner: number;
  /** Points the winner scored (card values, plus the last-trick bonus on trick 13). */
  points: number;
}

export interface HazariState {
  config: HazariConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: HazariPhase;
  hands: CardId[][];
  turn: number;
  trick: HazariTrickPlay[];
  tricks: HazariCompletedTrick[];
  /** Points each seat has taken so far this hand (public information). */
  pointsTaken: number[];
  totals: number[];
  /** Points every seat scored in each finished hand. */
  handResults: number[][];
}

export type HazariAction = { type: 'play'; card: CardId };

export type HazariLegal = { kind: 'play'; cards: CardId[] } | { kind: 'none' };

export interface HazariView {
  gameType: 'HAZARI';
  config: HazariConfig;
  seat: number | null;
  phase: HazariPhase;
  hand: number;
  dealer: number;
  turn: number;
  cards: CardId[];
  handCounts: number[];
  trick: HazariTrickPlay[];
  lastTrick: HazariCompletedTrick | null;
  tricks: HazariCompletedTrick[];
  pointsTaken: number[];
  handResults: number[][];
  totals: number[];
}

const DEFAULTS: HazariConfig = {
  targetScore: 500,
  maxHands: 12,
  handEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
export const HAZARI_LAST_TRICK_BONUS = 10;

/** Scoring value of a card: A 11, 10 10, K 4, Q 3, J 2, everything else 0. */
export function hazariCardPoints(card: CardId): number {
  switch (parseCard(card).rank) {
    case 14:
      return 11;
    case 10:
      return 10;
    case 13:
      return 4;
    case 12:
      return 3;
    case 11:
      return 2;
    default:
      return 0;
  }
}

export function hazariTrickWinner(plays: readonly HazariTrickPlay[]): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    if (c.suit === lead && c.rank > parseCard(best.card).rank) best = p;
  }
  return best.seat;
}

export function hazariLegalCards(hand: readonly CardId[], trick: readonly HazariTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  return followers.length > 0 ? followers : [...hand];
}

/** Lowest-value, lowest-rank legal card: what a timed-out or beginner seat plays. */
export function hazariCheapestCard(legal: readonly CardId[]): CardId {
  return [...legal].sort((a, b) => hazariCardPoints(a) - hazariCardPoints(b) || parseCard(a).rank - parseCard(b).rank)[0]!;
}

function deal(state: HazariState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  deck.forEach((c, i) => hands[i % SEATS]!.push(c));
  state.hands = hands.map((h) => sortHand(h));
  state.trick = [];
  state.tricks = [];
  state.pointsTaken = [0, 0, 0, 0];
  state.phase = 'PLAYING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

function highestFirst(totals: readonly number[]): Standing[] {
  const sorted = totals.map((score, seat) => ({ seat, score })).sort((a, b) => b.score - a.score);
  const out: Standing[] = [];
  sorted.forEach((s, i) => {
    const prev = out[i - 1];
    out.push({ seat: s.seat, score: s.score, place: prev && prev.score === s.score ? prev.place : i + 1 });
  });
  return out;
}

export const hazariEngine: GameEngine<HazariConfig, HazariState, HazariAction, HazariView, HazariLegal> = {
  gameType: 'HAZARI',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 20, 5000);
    checkInt('maxHands', c.maxHands, 1, 40);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Hazari needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: HazariState = {
      config,
      players,
      hand: 1,
      dealer: rng.int(SEATS),
      phase: 'PLAYING',
      hands: [],
      turn: 0,
      trick: [],
      tricks: [],
      pointsTaken: [0, 0, 0, 0],
      totals: [0, 0, 0, 0],
      handResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Hazari action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && state.phase === 'PLAYING', 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(hazariLegalCards(hand, state.trick).includes(action.card), 'ILLEGAL_MOVE', 'You must follow the led suit');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = hazariTrickWinner(state.trick);
    const last = state.tricks.length === 12;
    const points = state.trick.reduce((n, p) => n + hazariCardPoints(p.card), 0) + (last ? HAZARI_LAST_TRICK_BONUS : 0);
    state.pointsTaken[winner]! += points;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner, points });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length, points });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn || state.phase !== 'PLAYING') return { kind: 'none' };
    return { kind: 'play', cards: hazariLegalCards(state.hands[seat]!, state.trick) };
  },

  getTimeoutAction(state, seat) {
    return { type: 'play', card: hazariCheapestCard(hazariLegalCards(state.hands[seat]!, state.trick)) };
  },

  getView(state, seat) {
    return {
      gameType: 'HAZARI',
      config: state.config,
      seat,
      phase: state.phase,
      hand: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      cards: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      pointsTaken: [...state.pointsTaken],
      handResults: state.handResults.map((r) => [...r]),
      totals: [...state.totals],
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const scores = [...state.pointsTaken];
      state.handResults.push(scores);
      state.totals = state.totals.map((t, s) => t + scores[s]!);
      events.push({ type: 'handEnd', hand: state.hand, scores, totals: [...state.totals] });
      const highest = Math.max(...state.totals);
      const uniqueHighest = state.totals.filter((t) => t === highest).length === 1;
      const reached = highest >= cfg.targetScore;
      if ((reached && uniqueHighest) || state.hand >= cfg.maxHands) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: highestFirst(state.totals) });
      } else {
        state.phase = 'HAND_END';
      }
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return highestFirst(state.totals);
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
